import { useState, useEffect, useRef, useCallback } from "react";
import { getTaskStatus, cancelTask } from "./chatApi";

const POLL_INTERVAL_MS = 5000;

// Polls the research task status while a task is active and the chat is open.
// Owns the research status / website scrolls state, the polling interval and
// its cleanup, and exposes a cancel helper.
//
// `loadChat` and `dispatch` are read through refs so the effect does not
// restart on every render (they are recreated by the parent each render).
export default function useResearchPolling({ taskId, isChatOpen, selectedChatId, loadChat, dispatch }) {
  const [researchStatus, setResearchStatus] = useState(null); // 'pending' | 'in_progress' | 'done' | 'failed' | 'cancelled'
  const [websiteScrolls, setWebsiteScrolls] = useState([]);
  const intervalRef = useRef(null);

  const callbacksRef = useRef({ loadChat, dispatch });
  useEffect(() => {
    callbacksRef.current = { loadChat, dispatch };
  });

  const clearPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (taskId && isChatOpen) {
      const pollTaskStatus = async () => {
        const { loadChat: reloadChat, dispatch: doDispatch } = callbacksRef.current;
        try {
          const taskData = await getTaskStatus(taskId);
          const status = taskData.status;

          setResearchStatus(status);

          // If task is cancelled, stop polling
          if (status === "cancelled") {
            clearPolling();
            return;
          }

          // Update website scrolls if available
          if (taskData.website_scrolls && taskData.website_scrolls.length > 0) {
            setWebsiteScrolls(taskData.website_scrolls);
          }

          // Check if research summary is done (message was created)
          const researchSummaryDone = taskData.research_summary_done || false;

          // If task is done and summary message was created, reload messages
          if (status === "done" && researchSummaryDone && selectedChatId) {
            // Check for actions in task response (e.g., reload_space when items added to collection)
            if (taskData.actions) {
              // Handle refresh_chat action (explicit refresh request)
              if (taskData.actions.type === "refresh_chat") {
                // Reload messages immediately (small delay so Elasticsearch indexes the new message)
                setTimeout(() => {
                  if (selectedChatId) {
                    reloadChat(selectedChatId);
                  }
                }, 500);
              }
              // Handle reload_space action (when items are added to a collection)
              else if (taskData.actions.type === "reload_space") {
                const updatedSpaceId = taskData.actions.data?.space_id;
                const updates = taskData.actions.data?.updates || {};
                const shouldRefreshChat = taskData.actions.data?.refresh_chat || false;

                // Refresh spaces in sidebar
                doDispatch({ type: "GET_SPACES", payload: { performGetSpaces: true } });

                // Check if we're currently in the updated space
                let currentSpaceId = "";
                if (window.location.href.includes("/spaces/")) {
                  currentSpaceId = window.location.href.split("/").pop();
                }

                // Build success message
                let updateMessage = "Items added to collection successfully!";
                if (updates.items_added) {
                  updateMessage = `${updates.items_added} item${updates.items_added > 1 ? "s" : ""} added to collection!`;
                }

                doDispatch({
                  type: "SET_POPUP",
                  payload: { text: updateMessage, type: "success" },
                });

                // If we're in the updated space, trigger space reload
                if (currentSpaceId === updatedSpaceId) {
                  doDispatch({
                    type: "RELOAD_CURRENT_SPACE",
                    payload: {
                      spaceId: updatedSpaceId,
                      updates: updates,
                    },
                  });
                }

                // Clear popup after a delay
                setTimeout(() => {
                  doDispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
                }, 3000);

                // Refresh chat if requested
                if (shouldRefreshChat) {
                  setTimeout(() => {
                    if (selectedChatId) {
                      reloadChat(selectedChatId);
                    }
                  }, 500);
                }
              }
            } else {
              // No actions, but research is done - reload messages anyway
              setTimeout(() => {
                if (selectedChatId) {
                  reloadChat(selectedChatId);
                }
              }, 500);
            }

            // Stop polling
            clearPolling();
          }
          // If task is done but summary not created yet, keep polling —
          // the worker might still be creating the message.
          else if (status === "failed") {
            // Task failed, stop polling
            clearPolling();

            // Reload messages on failure to show any error messages
            if (selectedChatId) {
              setTimeout(() => {
                reloadChat(selectedChatId);
              }, 1000);
            }
          }
        } catch (error) {
          console.error("Error polling task status:", error);
          // Don't stop polling on error, just log it
        }
      };

      // Initial poll
      pollTaskStatus();

      // Set up polling interval
      intervalRef.current = setInterval(pollTaskStatus, POLL_INTERVAL_MS);

      // Cleanup on unmount or when task changes
      return clearPolling;
    }

    // No task or chat closed: make sure nothing keeps polling
    clearPolling();
    return undefined;
  }, [taskId, isChatOpen, selectedChatId]);

  // Reset research state when the chat closes
  useEffect(() => {
    if (!isChatOpen) {
      setResearchStatus(null);
      setWebsiteScrolls([]);
    }
  }, [isChatOpen]);

  const cancelResearch = useCallback(async () => {
    if (!taskId) return;
    try {
      await cancelTask(taskId);
      setResearchStatus("cancelled");
      clearPolling();
    } catch (error) {
      console.error("Failed to cancel task:", error);
    }
  }, [taskId]);

  return {
    researchStatus,
    setResearchStatus,
    websiteScrolls,
    setWebsiteScrolls,
    cancelResearch,
  };
}
